import React, { useEffect, useRef, useState } from "react";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "../firebase/firebase";
import { API_BACKEND_URL } from "../utils/envUtils";
import PasswordField from "../components/common/PasswordField";
import Welcome from "../components/welcome/Welcome";
import {
  selectErrorSession,
  selectIsLoading,
  selectUser,
} from "../store/slices/session/sessionSelector";
import { loginEmail, sessionError } from "../store/slices/session/sessionSlice";
import { setToastMessage } from "../store/slices/app/appSlice";

/**
 * Página del enlace de recuperación de contraseña.
 *
 * - /reset-password?token=...: enlace que genera el backend (POST /users/recover-password),
 *   válido 48 h y de un solo uso. Se valida y se cambia la contraseña con el backend.
 * - /reset-password?oobCode=...: código de Firebase (caduca a la hora), por si la página
 *   está configurada como "action URL" en la consola de Firebase.
 *
 * Se elige la nueva contraseña y, al confirmar, "Continuar" inicia sesión con ella.
 */

const MIN_LENGTH = 8;

// Errores de Firebase que significan que el enlace ya no sirve
const INVALID_LINK_CODES = [
  "auth/expired-action-code",
  "auth/invalid-action-code",
  "auth/user-disabled",
  "auth/user-not-found",
];
const WEAK_PASSWORD_CODES = [
  "auth/weak-password",
  "auth/password-does-not-meet-requirements",
];

type Step = "verifying" | "invalid" | "form" | "success";

const passwordRules = (password: string, confirmation: string) => ({
  length: password.length >= MIN_LENGTH,
  lettersAndNumbers: /[a-zA-Z]/.test(password) && /\d/.test(password),
  match: password.length > 0 && password === confirmation,
});

const errorCode = (error: unknown) =>
  (error as { code?: string } | null)?.code ?? "";

// Llamadas públicas al backend para los enlaces con token. Los errores se traducen a los
// mismos códigos que usa Firebase para reutilizar el manejo de errores.
const postPasswordReset = async (
  path: "verify" | "confirm",
  body: Record<string, string>
): Promise<string> => {
  const response = await fetch(
    `${API_BACKEND_URL}/api/password-reset/${path}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    const message = json?.message;
    let code = "unknown";
    if (message === "INVALID_LINK") code = "auth/invalid-action-code";
    if (message === "WEAK_PASSWORD") code = "auth/weak-password";
    throw Object.assign(new Error(message ?? "Password reset failed"), {
      code,
    });
  }
  return json?.data?.email ?? "";
};

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get("oobCode") ?? "";
  const token = searchParams.get("token") ?? "";

  const user = useSelector(selectUser);
  const errorSession = useSelector(selectErrorSession);
  const isLoggingIn = useSelector(selectIsLoading);

  const [step, setStep] = useState<Step>(
    token || oobCode ? "verifying" : "invalid"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isContinuing, setIsContinuing] = useState(false);
  // Usuario en sesión al pulsar "Continuar": se entra cuando cambia por el de la cuenta
  const userAtContinue = useRef(user);

  useEffect(() => {
    if (!token && !oobCode) return;
    let cancelled = false;
    const verify = token
      ? postPasswordReset("verify", { token })
      : verifyPasswordResetCode(auth, oobCode);
    verify
      .then((accountEmail) => {
        if (cancelled) return;
        setEmail(accountEmail);
        setStep("form");
      })
      .catch(() => {
        if (!cancelled) setStep("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token, oobCode]);

  // Login automático: cuando el saga deja en sesión la cuenta recién cambiada, se entra
  useEffect(() => {
    if (!isContinuing) return;
    if (
      user &&
      user !== userAtContinue.current &&
      user.email?.toLowerCase() === email.toLowerCase()
    ) {
      navigate("/", { replace: true });
    }
  }, [isContinuing, user, email, navigate]);

  // Si el login automático falla, la contraseña ya está cambiada: se manda a /login
  useEffect(() => {
    if (!isContinuing || !errorSession) return;
    dispatch(sessionError(null));
    dispatch(
      setToastMessage({
        message: t("resetPassword.errors.autoLogin"),
        severity: "warning",
      })
    );
    navigate("/login", { replace: true });
  }, [isContinuing, errorSession, dispatch, navigate, t]);

  const rules = passwordRules(password, confirmation);
  const canSubmit =
    rules.length && rules.lettersAndNumbers && rules.match && !isSaving;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setIsSaving(true);
    setFormError(null);
    try {
      if (token) {
        await postPasswordReset("confirm", { token, password });
      } else {
        await confirmPasswordReset(auth, oobCode, password);
      }
      setStep("success");
    } catch (error) {
      const code = errorCode(error);
      if (INVALID_LINK_CODES.includes(code)) {
        setStep("invalid");
      } else if (WEAK_PASSWORD_CODES.includes(code)) {
        setFormError(t("resetPassword.errors.weakPassword"));
      } else {
        setFormError(t("resetPassword.errors.generic"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleContinue = () => {
    userAtContinue.current = user;
    setIsContinuing(true);
    dispatch(loginEmail({ email, password }));
  };

  const cardSx = {
    width: "80%",
    maxWidth: 520,
    padding: "36px",
    backgroundColor: theme.palette.neutral.main,
    borderRadius: "16px",
  };

  const statusIcon = (color: string, icon: React.ReactNode) => (
    <Box
      sx={{
        width: 72,
        height: 72,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 3,
        color,
        // Halo suave del color del estado
        backgroundColor: alpha(color, 0.12),
        "& svg": { fontSize: 44 },
      }}
    >
      {icon}
    </Box>
  );

  const ruleItem = (met: boolean, label: string) => (
    <Box
      component="li"
      key={label}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        color: met ? theme.palette.success.main : theme.palette.text.secondary,
        transition: "color 150ms ease",
        "& svg": { fontSize: 18 },
      }}
    >
      {met ? <CheckCircleRoundedIcon /> : <RadioButtonUncheckedRoundedIcon />}
      <Typography variant="pMedium" color="inherit">
        {label}
      </Typography>
    </Box>
  );

  const renderContent = () => {
    if (step === "verifying") {
      return (
        <Box sx={{ ...cardSx, textAlign: "center" }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="pMedium" color="primary" component="p">
            {t("resetPassword.verifying")}
          </Typography>
        </Box>
      );
    }

    if (step === "invalid") {
      return (
        <Box sx={{ ...cardSx, textAlign: "center" }}>
          {statusIcon(theme.palette.error.main, <LinkOffRoundedIcon />)}
          <Typography
            variant="h3"
            fontWeight="bold"
            color="primary"
            sx={{ mb: 1.5 }}
          >
            {t("resetPassword.invalidTitle")}
          </Typography>
          <Typography variant="pMedium" color="primary" component="p">
            {t("resetPassword.invalidText")}
          </Typography>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
            onClick={() => navigate("/login", { replace: true })}
            endIcon={<ChevronRightIcon />}
          >
            {t("resetPassword.goToLogin")}
          </Button>
        </Box>
      );
    }

    if (step === "success") {
      return (
        <Box
          sx={{
            ...cardSx,
            textAlign: "center",
            "@keyframes resetPasswordPop": {
              "0%": { transform: "scale(0.6)", opacity: 0 },
              "70%": { transform: "scale(1.08)", opacity: 1 },
              "100%": { transform: "scale(1)" },
            },
            "& .reset-password-success-icon": {
              animation: "resetPasswordPop 380ms ease-out",
            },
          }}
        >
          <Box className="reset-password-success-icon">
            {statusIcon(theme.palette.success.main, <CheckCircleRoundedIcon />)}
          </Box>
          <Typography
            variant="h3"
            fontWeight="bold"
            color="primary"
            sx={{ mb: 1.5 }}
          >
            {t("resetPassword.successTitle")}
          </Typography>
          <Typography variant="pMedium" color="primary" component="p">
            {t("resetPassword.successText", { email })}
          </Typography>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
            onClick={handleContinue}
            disabled={isContinuing}
            startIcon={
              isContinuing && isLoggingIn ? (
                <CircularProgress size={20} />
              ) : null
            }
            endIcon={!isContinuing && <ChevronRightIcon />}
          >
            {isContinuing
              ? t("resetPassword.continuing")
              : t("resetPassword.continue")}
          </Button>
        </Box>
      );
    }

    return (
      <Box component="form" onSubmit={handleSubmit} noValidate sx={cardSx}>
        <Typography
          variant="h3"
          fontWeight="bold"
          color="primary"
          sx={{ mb: 1.5 }}
        >
          {t("resetPassword.title")}
        </Typography>
        <Typography variant="pMedium" color="primary" component="p">
          {t("resetPassword.subtitle")}
        </Typography>
        <Box
          sx={{
            mt: 2,
            mb: 3,
            px: 2,
            py: 1.25,
            borderRadius: "10px",
            backgroundColor: theme.palette.background.default,
            wordBreak: "break-all",
          }}
        >
          <Typography variant="pMedium" fontWeight="bold" color="primary">
            {email}
          </Typography>
        </Box>

        <Typography variant="pMedium" fontWeight="bold" color="primary">
          {t("resetPassword.newPassword")}
        </Typography>
        <PasswordField
          fullWidth
          autoFocus
          variant="outlined"
          margin="normal"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mt: 1 }}
        />

        <Typography variant="pMedium" fontWeight="bold" color="primary">
          {t("resetPassword.confirmPassword")}
        </Typography>
        <PasswordField
          fullWidth
          variant="outlined"
          margin="normal"
          autoComplete="new-password"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          error={confirmation.length > 0 && !rules.match}
          sx={{ mt: 1 }}
        />

        <Box
          component="ul"
          sx={{
            listStyle: "none",
            p: 0,
            m: 0,
            mt: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          {ruleItem(
            rules.length,
            t("resetPassword.rules.length", { count: MIN_LENGTH })
          )}
          {ruleItem(
            rules.lettersAndNumbers,
            t("resetPassword.rules.lettersAndNumbers")
          )}
          {ruleItem(rules.match, t("resetPassword.rules.match"))}
        </Box>

        {formError && (
          <Typography
            role="alert"
            variant="pMedium"
            component="p"
            sx={{ mt: 2, color: theme.palette.error.main }}
          >
            {formError}
          </Typography>
        )}

        <Button
          fullWidth
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 3 }}
          disabled={!canSubmit}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
          endIcon={!isSaving && <ChevronRightIcon />}
        >
          {isSaving ? t("resetPassword.saving") : t("resetPassword.submit")}
        </Button>
      </Box>
    );
  };

  return (
    <Grid2 container sx={{ minHeight: "100vh" }}>
      <Welcome />
      <Grid2
        component="div"
        size={{ xs: 12, md: 6 }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 4,
          backgroundColor: theme.palette.background.default,
        }}
      >
        {renderContent()}
      </Grid2>
    </Grid2>
  );
};

export default ResetPassword;
